import React, { useState } from "react";
import { Link } from "react-router-dom";
import { personalProjects } from "../data/mock";
import { ArrowUpRight, Sparkle, Plus, Check } from "lucide-react";

// Mirrors the Work grid but pitched as side explorations / concept work
const PersonalProjects = () => {
  const [showAll, setShowAll] = useState(false);
  if (!personalProjects || personalProjects.length === 0) return null;
  const initial = 2;
  const visible = showAll ? personalProjects : personalProjects.slice(0, initial);
  const hasMore = personalProjects.length > initial;

  return (
    <section
      id="personal"
      className="py-24 lg:py-32 border-t border-neutral-100 dark:border-neutral-800 bg-white dark:bg-neutral-950"
    >
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="max-w-[820px]">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-neutral-500 dark:text-neutral-400">
            <Sparkle className="w-3 h-3" strokeWidth={2.25} />
            Personal projects
          </div>
          <h2 className="mt-4 text-[34px] sm:text-[44px] lg:text-[56px] leading-[1.05] tracking-[-0.03em] font-medium text-neutral-900 dark:text-neutral-50">
            What I design when{" "}
            <span
              style={{ fontFamily: "'Segoe Script', 'Caveat', cursive" }}
              className="font-normal text-neutral-600 dark:text-neutral-400"
            >
              no one’s briefing me.
            </span>
          </h2>
          <p className="mt-4 text-[16px] sm:text-[17px] text-neutral-600 dark:text-neutral-400 max-w-[640px] leading-[1.6]">
            Side explorations and concept projects — the same product-thinking
            craft, on problems I find worth solving.
          </p>
        </div>

        {/* Cards */}
        <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-6">
          {visible.map((p, idx) => (
            <PersonalProjectCard key={p.id} p={p} idx={idx} />
          ))}
        </div>

        {/* Load more */}
        {hasMore && (
          <div className="mt-12 flex flex-col items-center gap-3">
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              data-testid="load-more-personal-button"
              className="group inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 text-neutral-900 dark:text-neutral-50 hover:border-neutral-300 dark:hover:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-900 text-[14px] font-medium transition-colors duration-200"
            >
              {showAll ? (
                <>
                  <Check className="w-4 h-4" strokeWidth={2.25} />
                  Showing all projects
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 transition-transform duration-200 group-hover:rotate-90" strokeWidth={2.25} />
                  Load More Projects
                </>
              )}
            </button>
            <span className="text-[12px] text-neutral-400 dark:text-neutral-500">
              {showAll
                ? `${personalProjects.length} of ${personalProjects.length} projects`
                : `${initial} of ${personalProjects.length} projects`}
            </span>
          </div>
        )}
      </div>
    </section>
  );
};

const PersonalProjectCard = ({ p, idx }) => {
  const isLaptop = p.cardAspect === "laptop" || p.screenAspect === "laptop";
  return (
    <Link
      to={`/work/${p.id}`}
      data-testid={`personal-project-card-${p.id}`}
      className="group relative block bg-white dark:bg-neutral-950 border border-neutral-200 dark:border-neutral-800 rounded-2xl overflow-hidden hover:border-neutral-300 dark:hover:border-neutral-700 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_30px_60px_-30px_rgba(0,0,0,0.18)] dark:hover:shadow-[0_30px_60px_-30px_rgba(0,0,0,0.6)]"
    >
      {/* Visual — phone-framed mockup or laptop preview on a soft backdrop */}
      <div className="relative h-[340px] lg:h-[380px] overflow-hidden border-b border-neutral-200 dark:border-neutral-800 bg-gradient-to-br from-neutral-50 to-neutral-100 dark:from-neutral-900 dark:to-neutral-950 flex items-center justify-center">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(0,0,0,0.05)_1px,transparent_0)] dark:bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.05)_1px,transparent_0)] [background-size:22px_22px] opacity-70" />
        <img
          src={p.image}
          alt={`${p.name} cover`}
          loading="lazy"
          className={`relative ${isLaptop ? "max-h-[80%] max-w-[92%] object-contain" : "max-h-[90%] max-w-[70%] object-contain"} transition-transform duration-700 ease-out group-hover:scale-[1.03]`}
        />
        {/* Personal badge */}
        <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/95 dark:bg-neutral-900/95 backdrop-blur border border-neutral-200 dark:border-neutral-800 text-[10.5px] uppercase tracking-[0.18em] text-neutral-700 dark:text-neutral-300">
          <Sparkle className="w-3 h-3" strokeWidth={2.25} />
          Personal
        </span>
      </div>

      <div className="p-6 lg:p-7">
        <div className="flex items-center justify-between">
          <span className="text-[12px] font-mono uppercase tracking-widest text-neutral-500 dark:text-neutral-400">
            {p.year}
          </span>
          <span className="text-[12px] font-mono text-neutral-400 dark:text-neutral-500">
            P0{idx + 1}
          </span>
        </div>

        <h3 className="mt-3 text-[26px] lg:text-[30px] font-medium tracking-tight text-neutral-900 dark:text-neutral-50 leading-[1.1]">
          {p.name}
        </h3>

        <div className="mt-7 pt-5 border-t border-neutral-100 dark:border-neutral-800 flex items-center justify-between">
          <span className="inline-flex items-center px-2.5 py-1 rounded-full border border-neutral-200 dark:border-neutral-800 text-[11.5px] uppercase tracking-widest text-neutral-600 dark:text-neutral-400">
            {p.tag}
          </span>
          <span className="inline-flex items-center gap-1.5 text-[13.5px] font-medium text-neutral-900 dark:text-neutral-50 group-hover:text-neutral-600 dark:group-hover:text-neutral-300 transition-colors duration-200">
            Read case study
            <ArrowUpRight
              className="w-3.5 h-3.5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              strokeWidth={2.25}
            />
          </span>
        </div>
      </div>
    </Link>
  );
};

export default PersonalProjects;
